import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
} from '@components/Common/EntityWorkspace';
import type { GlanceRow, WorkspaceTab } from '@components/Common/EntityWorkspace';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import ErrorMessage from '@components/Common/ErrorMessage';
import type { EmailTemplateListItem } from '@components/EmailTemplates/types';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  fetchDripSequences,
  fetchRegistrySources,
  type DripSequence,
  type DripSequenceStats,
} from './api';
import { emailAudienceLabel } from '@helpers/emailAudience';
import './index.scss';

const TABS: WorkspaceTab[] = [{ id: 'steps', label: 'Steps', icon: <CampaignOutlinedIcon /> }];

/**
 * One drip sequence: its cadence, its enrollment counts, and its steps.
 *
 * Every step links out to the ordinary email record, so a step's content and performance are read
 * on the same screen as any other email rather than in a second, sequence-shaped copy of it.
 */
const DripSequenceRecord: React.FC = () => {
  const { sequenceId = '' } = useParams<{ sequenceId: string }>();
  const navigate = useNavigate();

  const [sequence, setSequence] = useState<DripSequence | null>(null);
  const [stats, setStats] = useState<DripSequenceStats | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([]);
  const [activeTab, setActiveTab] = useState('steps');
  const [activityOpen, toggleActivity] = useActivityRail();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [drip, sources] = await Promise.all([fetchDripSequences(), fetchRegistrySources()]);
      const seq = (drip.sequences ?? []).find((s) => s.campaignType === sequenceId) ?? null;
      if (!seq) {
        setError('That drip sequence does not exist.');
        return;
      }
      setSequence(seq);
      setStats(drip.stats?.[sequenceId] ?? null);
      setTemplates(sources.templates ?? []);
    } catch {
      setError('Could not load this sequence. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sequenceId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Step names live only on the templates — the sequence itself carries offsets and keys. */
  const nameByKey = useMemo(() => {
    const m = new Map<string, string>();
    templates.forEach((t) => m.set(t.templateKey, t.name));
    return m;
  }, [templates]);

  const glance: GlanceRow[] = useMemo(() => {
    if (!sequence) return [];
    const last = sequence.steps[sequence.steps.length - 1];
    return [
      { key: 'Audience', value: emailAudienceLabel(sequence.audience) },
      {
        key: 'Steps',
        value: `${sequence.stepCount} · day ${sequence.steps[0]?.dayOffset ?? 0}–${last?.dayOffset ?? 0}`,
      },
      { key: 'Active', value: String(stats?.byStatus?.ACTIVE ?? 0), mono: true },
      { key: 'Completed', value: String(stats?.byStatus?.COMPLETED ?? 0), mono: true },
    ];
  }, [sequence, stats]);

  if (loading) return <SkeletonLoader variant="workspace" />;
  if (error) return <ErrorMessage page message={error} />;
  if (!sequence) return <ErrorMessage page message="Sequence not found." />;

  const identityRail = (
    <IdentityRail
      glance={glance}
      initials="DR"
      name={sequence.name}
      subId={sequence.campaignType}
    />
  );

  return (
    <EntityWorkspace
      activeTabId={activeTab}
      activityOpen={activityOpen}
      activityRail={<ActivityRail timeline={[]} />}
      backLabel="Drip sequences"
      identityRail={identityRail}
      tabs={TABS}
      onBack={() => navigate('/email-campaigns/drip-sequences')}
      onTabChange={setActiveTab}
      onToggleActivity={toggleActivity}
    >
      <SectionCard
        icon={<CampaignOutlinedIcon />}
        meta={`${sequence.stepCount} steps`}
        title="Steps"
      >
        <Box sx={{ width: '100%' }}>
          {sequence.steps.map((step) => (
            <Box key={step.templateKey} className="eo-step">
              <span className="eo-step__day">D{step.dayOffset}</span>
              <Box className="eo-step__body">
                <div className="eo-step__name">
                  {nameByKey.get(step.templateKey) ?? `Step ${step.step}`}
                </div>
                <div className="eo-step__key">{step.templateKey}</div>
              </Box>
              {/* Enrollment counts are "people sitting on this step right now", not "people who
                  have ever received it" — the enrollment stores a current step, not a history. */}
              <span className="eo-step__count">
                {stats?.activeByStep?.[String(step.step)] ?? 0} here now
              </span>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  navigate(`/email-campaigns/emails/${encodeURIComponent(step.templateKey)}`)
                }
              >
                Open template
              </Button>
            </Box>
          ))}
        </Box>
      </SectionCard>

      <Typography color="text.secondary" sx={{ mt: 1.5, display: 'block' }} variant="caption">
        Cadence is fixed in the sending service, so steps cannot be added, reordered or re-timed
        here.
      </Typography>
    </EntityWorkspace>
  );
};

export default DripSequenceRecord;
